import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API, USER_ID } from "../App";
import { 
  Fire, 
  Lightning, 
  Drop,
  Bread,
  Timer,
  TrendUp,
  TrendDown,
  Minus,
  Plus,
  Barbell,
  ForkKnife
} from "@phosphor-icons/react";
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  ResponsiveContainer,
  Tooltip 
} from "recharts";

const Dashboard = ({ profile }) => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        const response = await axios.get(`${API}/dashboard/${USER_ID}`);
        setDashboardData(response.data);
      } catch (error) {
        console.error("Error fetching dashboard:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchDashboard();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gold animate-pulse-slow">Chargement...</div>
      </div>
    );
  }

  const nutrition = dashboardData?.nutrition || { calories: 0, protein: 0, carbs: 0, fat: 0, goals: { calories: 2000, protein: 150, carbs: 250, fat: 65 } };
  const workout = dashboardData?.workout || { sessions_count: 0, calories_burned: 0, duration_minutes: 0 };
  const weight = dashboardData?.weight || { current: null, goal: null, history: [] };

  const calorieProgress = Math.min((nutrition.calories / nutrition.goals.calories) * 100, 100);
  const proteinProgress = Math.min((nutrition.protein / nutrition.goals.protein) * 100, 100);
  const carbsProgress = Math.min((nutrition.carbs / nutrition.goals.carbs) * 100, 100);
  const fatProgress = Math.min((nutrition.fat / nutrition.goals.fat) * 100, 100);

  const weightTrend = weight.history.length >= 2 
    ? weight.history[weight.history.length - 1]?.weight - weight.history[0]?.weight 
    : 0;

  const firstName = profile?.name?.split(" ")[0];
  const headline = firstName ? `Salut ${firstName} !` : "Salut !";
  const intro = firstName
    ? "Ton espace du jour est prêt : regarde tes chiffres, ajuste ton alimentation et garde le cap."
    : "Ton espace du jour est prêt : regarde tes chiffres, ajuste ton alimentation et garde le cap.";

  return (
    <div className="p-4 md:p-8 animate-fadeIn max-w-6xl mx-auto">
      <div className="dashboard-hero mb-6" data-testid="dashboard-hero">
        <div>
          <p className="dashboard-kicker" data-testid="dashboard-date-label">
          {new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
          </p>
          <h1 className="font-display text-4xl uppercase tracking-tight text-[#0A0A0A] md:text-5xl" data-testid="dashboard-greeting">
            {headline}
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-7 text-gray-600 md:text-base" data-testid="dashboard-intro">
            {intro}
          </p>
        </div>

        <div className="dashboard-hero-badge" data-testid="dashboard-focus-card">
          <p className="dashboard-kicker">Focus du jour</p>
          <p className="mt-2 text-2xl font-semibold text-white">Nutrition + entraînement + régularité</p>
          <p className="mt-3 text-sm text-white/70">Fais simple : remplis tes repas, note ta séance et regarde où tu en es.</p>
        </div>
      </div>

      {/* Main Calories Card */}
      <div className="card p-6 mb-6" data-testid="calories-card">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-sm text-gray-500">Calories aujourd'hui</p>
            <div className="flex items-baseline gap-2 mt-1">
              <span className="text-4xl font-bold text-charcoal">{Math.round(nutrition.calories)}</span>
              <span className="text-gray-400">/ {nutrition.goals.calories} kcal</span>
            </div>
          </div>
          <div className="w-16 h-16 bg-gold-light rounded-2xl flex items-center justify-center">
            <Fire size={32} weight="fill" className="text-gold" />
          </div>
        </div>
        
        <div className="progress-bar mb-2">
          <div className="progress-bar-fill animate-progress" style={{ width: `${calorieProgress}%` }} />
        </div>
        <p className="text-sm text-gray-500">
          {Math.round(nutrition.goals.calories - nutrition.calories)} kcal restantes
        </p>
      </div>

      {/* Macros Grid */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="card p-4" data-testid="protein-card">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
              <Lightning size={18} weight="fill" className="text-red-500" />
            </div>
          </div>
          <p className="text-xs text-gray-500 uppercase tracking-wider">Protéines</p>
          <p className="text-xl font-bold text-charcoal">{Math.round(nutrition.protein)}g</p>
          <div className="h-1 bg-gray-100 rounded-full mt-2 overflow-hidden">
            <div className="h-full bg-red-400 rounded-full" style={{ width: `${proteinProgress}%` }} />
          </div>
        </div>

        <div className="card p-4" data-testid="carbs-card">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 bg-teal-100 rounded-lg flex items-center justify-center">
              <Bread size={18} weight="fill" className="text-teal-500" />
            </div>
          </div>
          <p className="text-xs text-gray-500 uppercase tracking-wider">Glucides</p>
          <p className="text-xl font-bold text-charcoal">{Math.round(nutrition.carbs)}g</p>
          <div className="h-1 bg-gray-100 rounded-full mt-2 overflow-hidden">
            <div className="h-full bg-teal-400 rounded-full" style={{ width: `${carbsProgress}%` }} />
          </div>
        </div>

        <div className="card p-4" data-testid="fat-card">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center">
              <Drop size={18} weight="fill" className="text-amber-500" />
            </div>
          </div>
          <p className="text-xs text-gray-500 uppercase tracking-wider">Lipides</p>
          <p className="text-xl font-bold text-charcoal">{Math.round(nutrition.fat)}g</p>
          <div className="h-1 bg-gray-100 rounded-full mt-2 overflow-hidden">
            <div className="h-full bg-amber-400 rounded-full" style={{ width: `${fatProgress}%` }} />
          </div>
        </div>
      </div>

      {/* Workout & Weight Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        {/* Today's Workout */}
        <div className="card p-5" data-testid="workout-summary">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-charcoal">Entraînement du jour</h3>
            <Barbell size={20} className="text-gold" weight="fill" />
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <p className="text-2xl font-bold text-charcoal">{workout.sessions_count}</p>
              <p className="text-xs text-gray-500">Séances</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-charcoal">{workout.duration_minutes}</p>
              <p className="text-xs text-gray-500">Minutes</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-gold">{workout.calories_burned}</p>
              <p className="text-xs text-gray-500">Kcal brûlées</p>
            </div>
          </div>
        </div>

        {/* Weight Progress */}
        <div className="card p-5" data-testid="weight-progress">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-charcoal">Poids</h3>
            {weightTrend < 0 ? (
              <TrendDown size={20} className="text-green-500" weight="bold" />
            ) : weightTrend > 0 ? (
              <TrendUp size={20} className="text-red-500" weight="bold" />
            ) : (
              <Minus size={20} className="text-gray-400" weight="bold" />
            )}
          </div>
          <div className="flex items-end gap-6">
            <div>
              <p className="text-3xl font-bold text-charcoal">
                {weight.current ? `${weight.current}` : '--'}
                <span className="text-lg font-normal text-gray-400 ml-1">kg</span>
              </p>
              <p className="text-xs text-gray-500">Actuel</p>
            </div>
            {weight.goal && (
              <div>
                <p className="text-xl font-bold text-gold">{weight.goal} kg</p>
                <p className="text-xs text-gray-500">Objectif</p>
              </div>
            )}
          </div>
          {weight.history.length > 1 && (
            <div className="h-20 mt-4">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={weight.history}>
                  <Line 
                    type="monotone" 
                    dataKey="weight" 
                    stroke="#F5B83D" 
                    strokeWidth={2}
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 gap-4">
        <Link 
          to="/nutrition"
          className="card p-5 flex items-center gap-4 card-hover"
          data-testid="quick-add-meal"
        >
          <div className="w-12 h-12 bg-gold-light rounded-xl flex items-center justify-center">
            <ForkKnife size={24} className="text-gold" weight="fill" />
          </div>
          <div>
            <p className="font-semibold text-charcoal">Ajouter un repas</p>
            <p className="text-sm text-gray-500">Suivi nutritionnel</p>
          </div>
        </Link>

        <Link 
          to="/workouts"
          className="card p-5 flex items-center gap-4 card-hover"
          data-testid="quick-add-workout"
        >
          <div className="w-12 h-12 bg-gold-light rounded-xl flex items-center justify-center">
            <Barbell size={24} className="text-gold" weight="fill" />
          </div>
          <div>
            <p className="font-semibold text-charcoal">Nouvelle séance</p>
            <p className="text-sm text-gray-500">Entraînement</p>
          </div>
        </Link>
      </div>
    </div>
  );
};

export default Dashboard;
